import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { fetchWithAuth } from '../../utils/auth';

interface Payment {
  id: string;
  amount: number;
  status: 'pending' | 'completed' | 'failed';
  method: string;
  date: string;
  description: string;
}

const Pagamentos: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPreview, setIsPreview] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'all' | 'completed' | 'pending'>('all');

  useEffect(() => {
    let cancelled = false;

    const mapStatus = (raw: string | undefined): Payment['status'] => {
      const s = (raw || '').toLowerCase();
      if (['completed', 'paid', 'success'].includes(s)) return 'completed';
      if (['pending', 'processing', 'awaiting', 'created'].includes(s)) return 'pending';
      return 'failed';
    };

    const normalizePayment = (p: any): Payment => ({
      id: String(p?.id ?? p?.paymentId ?? p?._id ?? p?.reference ?? Math.random().toString(36).slice(2)),
      amount: Number(p?.amount ?? p?.value ?? p?.price ?? 0),
      status: mapStatus(p?.status ?? p?.state ?? p?.paymentStatus),
      method: String(p?.paymentType ?? p?.method ?? p?.channel ?? '—'),
      date: String(p?.date ?? p?.createdAt ?? p?.paidAt ?? new Date().toISOString()),
      description: String(p?.description ?? p?.planName ?? p?.productName ?? '—'),
    });

    async function fetchPayments() {
      setLoading(true);
      setIsPreview(false);
      try {
        const res = await fetchWithAuth('/api/payments/getUserPayments');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const rawList = Array.isArray(data) ? data : (data?.payments ?? []);
        const normalized = rawList.map(normalizePayment);
        if (!cancelled) {
          if (normalized.length) {
            setPayments(normalized);
            setIsPreview(false);
          } else {

            setIsPreview(true);
          }
        }
      } catch (err) {
        console.error('Erro ao obter pagamentos:', err);
        if (!cancelled) {
          // fallback to mock data for preview
          setIsPreview(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchPayments();
    return () => { cancelled = true; };
  }, []);

  const filteredPayments = payments.filter(payment => {
    if (activeFilter === 'all') return true;
    return payment.status === activeFilter;
  });

  const handleDownloadReceipt = async (payment: Payment) => {
    try {
      const res = await fetchWithAuth(`/api/payments/${payment.id}/receipt`);

      if (res.ok) {
        const data = await res.json();
        if (data.url) {
          window.open(data.url, '_blank', 'noopener,noreferrer');
          return;
        }
      }
      console.error('Recibo indisponível para este pagamento no Moloni.');
      alert('O recibo ainda está a ser gerado ou está indisponível para este pagamento.');
    } catch (e) {
      console.error('Erro ao descarregar recibo:', e);
      alert('Ocorreu um erro ao tentar descarregar o recibo.');
    }
  };

  const getStatusBadge = (status: Payment['status']) => {
    const styles = {
      completed: 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30',
      pending: 'bg-amber-500/10 text-amber-200 border border-amber-500/30',
      failed: 'bg-rose-500/10 text-rose-200 border border-rose-500/30',
    };
    const labels = {
      completed: 'Concluído',
      pending: 'Pendente',
      failed: 'Falhou',
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  if (loading) {
    return <div className="flex justify-center items-center py-12">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
    </div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-white">Histórico de Pagamentos</h2>
          <p className="text-xs sm:text-sm text-[#94a3b8]">
            {isPreview ? 'Pré-visualização com dados de exemplo' : 'Dados em tempo real da sua conta'}
          </p>
        </div>
        <div className="flex gap-1 sm:gap-2 bg-[#0f172a] border border-[#111827] rounded-full p-1 self-start sm:self-auto">
          {[
            { id: 'all', label: 'Todos' },
            { id: 'completed', label: 'Concluídos' },
            { id: 'pending', label: 'Pendentes' },
          ].map((filter) => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id as typeof activeFilter)}
              className={`px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors ${
                activeFilter === filter.id
                  ? 'bg-gradient-to-r from-[#8b5cf6] to-[#ec4899] text-white shadow-[0_10px_30px_rgba(139,92,246,0.35)]'
                  : 'text-[#cbd5e1] hover:text-white'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-[#111827] bg-[#0b1220] shadow-[0_12px_40px_rgba(0,0,0,0.35)] overflow-x-auto w-full block">
        <Table>
          <TableCaption className="px-4 pb-4">Valores em EUR; datas no fuso PT.</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">Data</TableHead>
              <TableHead className="whitespace-nowrap">Descrição</TableHead>
              <TableHead className="whitespace-nowrap">Método</TableHead>
              <TableHead className="text-right whitespace-nowrap">Valor</TableHead>
              <TableHead className="whitespace-nowrap">Status</TableHead>
              <TableHead className="text-right whitespace-nowrap">Recibo</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredPayments.map((payment) => (
              <TableRow key={payment.id}>
                <TableCell className="text-[#cbd5e1] whitespace-nowrap">
                  {new Date(payment.date).toLocaleDateString('pt-PT', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}
                </TableCell>
                <TableCell className="font-medium text-white whitespace-nowrap">{payment.description}</TableCell>
                <TableCell className="text-[#cbd5e1] whitespace-nowrap">{payment.method}</TableCell>
                <TableCell className="text-right font-semibold text-white whitespace-nowrap">€{payment.amount.toFixed(2)}</TableCell>
                <TableCell className="whitespace-nowrap">{getStatusBadge(payment.status)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {payment.status === 'completed' ? (
                    <button
                      onClick={() => handleDownloadReceipt(payment)}
                      className="inline-flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-[0.68rem] sm:text-xs font-medium bg-gradient-to-r from-[#8b5cf6] to-[#ec4899] text-white hover:brightness-110 transition"
                    >
                      <span className="hidden sm:inline">Descarregar recibo</span>
                      <span className="sm:hidden">Recibo</span>
                    </button>
                  ) : (
                    <span className="text-[#64748b] text-xs">—</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {filteredPayments.length === 0 && (
        <div className="text-center py-12 text-[#94a3b8] bg-[#0f172a] border border-[#111827] rounded-xl">
          <p className="text-lg">Nenhum pagamento encontrado</p>
          <p className="text-sm mt-1">Os filtros podem estar a ocultar resultados.</p>
        </div>
      )}
    </div>
  );
};

export default Pagamentos;
